import {Sector, SectorType} from "crqlar-react-common/dist/models/sector/sector.model";
import {
    daySlices,
    getAvailableTimeRangesPerSectorType,
    getAvailableTimeRangesPerViewMode,
    getAvailableViewModesPerSectorType,
    getTimelineSpanDates,
} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {useCallback, useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useDispatch} from "react-redux";
import {ReservationActions, useReservationsState} from "../../../../store/reservations.reducer";
import {getPermittedSteps, useCanSliceByPartOfDay} from "../../../../utils/reservation.util";
import type {
    CalendarDayPart,
    CalendarDayPartOption,
    CalendarViewCapabilities,
    CalendarViewMode,
    CalendarViewPickerProps,
    CalendarViewSettings,
    CalendarViewSpan,
} from "../../rail/CalendarViewPicker";

const DAY_PARTS: readonly CalendarDayPart[] = [
    "all_day",
    "morning",
    "day",
    "evening",
    "custom",
];

interface UseCalendarViewPickerStateParams {
    appId: string;
    sector?: Sector;
    focusedDate: Date;
}

export function useCalendarViewPickerState({
    appId,
    sector,
    focusedDate,
}: UseCalendarViewPickerStateParams): CalendarViewPickerProps {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const canSliceByPartOfDay = useCanSliceByPartOfDay(sector);
    const {
        viewMode,
        timelineSpan,
        timelineStep,
        reservationDaySlice,
        startTimeFrame,
        endTimeFrame,
    } = useReservationsState(appId);
    const availableViewModes = useMemo<CalendarViewMode[]>(
        () => getAvailableViewModesPerSectorType(sector?.sectorType) as CalendarViewMode[],
        [sector?.sectorType]
    );
    const selectedViewMode = availableViewModes.includes(viewMode as CalendarViewMode)
        ? viewMode as CalendarViewMode
        : availableViewModes[0] ?? "timeline";
    const availableViewSpans = useMemo<CalendarViewSpan[]>(
        () => (selectedViewMode === "timeGrid"
            ? getAvailableTimeRangesPerViewMode(selectedViewMode)
            : getAvailableTimeRangesPerSectorType(sector?.sectorType)) as CalendarViewSpan[],
        [sector?.sectorType, selectedViewMode]
    );
    const selectedViewSpan = availableViewSpans.includes(timelineSpan as CalendarViewSpan)
        ? timelineSpan as CalendarViewSpan
        : availableViewSpans[0] ?? "day";
    const selectedDayPart = DAY_PARTS.includes(reservationDaySlice as CalendarDayPart)
        ? reservationDaySlice as CalendarDayPart
        : "all_day";
    const value = useMemo<CalendarViewSettings>(() => ({
        mode: selectedViewMode,
        span: selectedViewSpan,
        stepMinutes: timelineStep ?? 30,
        dayPart: selectedDayPart,
        customStartTime: startTimeFrame ?? "00:00",
        customEndTime: endTimeFrame ?? "23:59",
    }), [
        endTimeFrame,
        selectedDayPart,
        selectedViewMode,
        selectedViewSpan,
        startTimeFrame,
        timelineStep,
    ]);
    const dayPartOptions = useMemo<CalendarDayPartOption[]>(() => DAY_PARTS.map(dayPart => {
        const [startTime, endTime] = daySlices(dayPart, "day");
        const label = sector?.sectorType === SectorType.RESTAURANT
            ? t(`Day part restaurant label ${dayPart}`)
            : t(`Day part ${dayPart}`);

        return dayPart === "custom" || dayPart === "all_day"
            ? {value: dayPart, label}
            : {value: dayPart, label, startTime, endTime};
    }), [sector?.sectorType, t]);
    const capabilities = useMemo<CalendarViewCapabilities>(() => ({
        modes: availableViewModes,
        spans: availableViewSpans,
        steps: getPermittedSteps(selectedViewSpan),
        dayParts: canSliceByPartOfDay ? dayPartOptions : [],
    }), [availableViewModes, availableViewSpans, canSliceByPartOfDay, dayPartOptions, selectedViewSpan]);

    const onChange = useCallback((nextSettings: CalendarViewSettings) => {
        const nextAvailableSpans = (nextSettings.mode === "timeGrid"
            ? getAvailableTimeRangesPerViewMode(nextSettings.mode)
            : getAvailableTimeRangesPerSectorType(sector?.sectorType)) as CalendarViewSpan[];
        const nextSpan = nextAvailableSpans.includes(nextSettings.span)
            ? nextSettings.span
            : nextAvailableSpans[0] ?? "day";
        const permittedSteps = getPermittedSteps(nextSpan);
        const nextStep = permittedSteps.includes(nextSettings.stepMinutes)
            ? nextSettings.stepMinutes
            : permittedSteps[0];
        const [nextFromDate, nextToDate] = getTimelineSpanDates(nextSpan, focusedDate);

        if (sector?.id) {
            localStorage.setItem(`viewMode-${sector.id}`, nextSettings.mode);
            localStorage.setItem(`timelineSpan-${sector.id}`, nextSpan);
            localStorage.setItem(`timelineStep-${sector.id}`, String(nextStep));
            localStorage.setItem(`reservationDaySlice-${sector.id}`, nextSettings.dayPart);
            localStorage.setItem(`startTimeFrame-${sector.id}`, nextSettings.customStartTime);
            localStorage.setItem(`endTimeFrame-${sector.id}`, nextSettings.customEndTime);
        }

        dispatch({
            type: ReservationActions.SET_RESERVATIONS,
            appId,
            payload: {
                viewMode: nextSettings.mode,
                timelineSpan: nextSpan,
                timelineStep: nextStep,
                reservationDaySlice: nextSettings.dayPart,
                startTimeFrame: nextSettings.customStartTime,
                endTimeFrame: nextSettings.customEndTime,
                fromDate: nextFromDate.toISOString(),
                toDate: nextToDate.toISOString(),
            },
        });
    }, [appId, dispatch, focusedDate, sector?.id, sector?.sectorType]);

    return {value, capabilities, onChange};
}
